import { Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import { ModalIcon, ModalKeys, NotificationService } from '@shared/notification/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { TimberService } from '@logging/timber.service';
import { PlatformLoginService } from '../platform-login/platform-login.service';
import { LogoutService } from '../authentication/logout.service';
import { ShellContextFacade } from '../shell-context/shell-context-facade.service';

const MODULE_NAME = 'CompanyAccessService';

/**
 * Verifies (for the new iTero Platform Login flow) that the logged-in user is allowed to access
 * the Lab platform. If not, it notifies the user and redirects to the platform login page.
 * Used only by the new login flow; the legacy flow does not call this.
 */
@Injectable({ providedIn: 'root' })
export class CompanyAccessService {
	constructor(
		private readonly platformLoginService: PlatformLoginService,
		private readonly notificationService: NotificationService,
		private readonly translateService: TranslateService,
		private readonly timberService: TimberService,
		private readonly logoutService: LogoutService,
		private readonly shellContextFacade: ShellContextFacade
	) {}

	async verifyOrRedirect(): Promise<boolean> {
		try {
			const accessToken = this.shellContextFacade.sessionInfo?.accessToken;
			const canAccess = await this.platformLoginService.canUserAccessLabPlatform(accessToken);

			if (!canAccess) {
				this.notificationService
					.show(
						this.translateService.instant('ExpectedRoles.RolesWarningHeader'),
						this.translateService.instant('ExpectedRoles.RolesWarningMessage'),
						{ buttonCombination: ModalKeys.Ok, icon: ModalIcon.Info }
					)
					.pipe(take(1))
					.subscribe(() => this.logoutService.logoutWithoutSessionInvalidation());
			}

			return canAccess;
		} catch (err) {
			this.timberService.error('Error verifying lab platform access.', { module: MODULE_NAME, error: err });

			return false;
		}
	}
}
